import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Modal, Row, Col, Pagination } from 'react-bootstrap';
import { ImageFile, ImageUsage } from '../../Models/Models';
import { Constants } from '../../Models/Constants';
import { SiteContext, ToastContext } from '../../Contexts';
import { GalleryService } from '../../Services/GalleryService';
import ImageSelectorComponent from '../Image/ImageSelector';
import DeleteButton from '../Buttons/DeleteButton';
import DiscardButton from '../Buttons/DiscardButton';
import SaveButton from '../Buttons/SaveButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes, faStar, faImage, faImages, faPlus, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { EntityColors } from '../../Themes/EntityColors';
import CustomTooltip from '../Tooltips/CustomTooltip';
import { Utils } from '../../Services/Utils';
import CreateButton from '../Buttons/CreateButton';
import ImageUploadComponent from '../Image/ImageUpload';
import GalleryDeleteModal from '../Modals/GalleryDeleteModal';
import GalleryTitleImageCropper, { TITLE_CROP } from './GalleryTitleCropper';
import FormShell from '../Form/FormShell';
import ToggleSwitch from '../ToggleSwitch';
import RequiredLabel from "../Form/RequiredLabel";
import { useIsDirty } from '../../Hooks/useIsDirty';
import { confirmDiscardChanges } from '../../Hooks/useUnsavedChangesGuard';

const IMAGES_PER_PAGE = 8;

type MoveControl = 'prev' | 'next' | 'position';

interface MovedImage {
    imageId: number;
    key: number;
    refocus?: MoveControl;
}

const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

const GalleryCreateEditComponent = ({ isEditMode, isPopUp = false, onClose }: { isEditMode: boolean; isPopUp?: boolean; onClose?: any }) => {
    useEffect(() => {
        if (isEditMode) Utils.setDocumentTitle('Úprava galérie');
        else Utils.setDocumentTitle('Tvorba galérie');
    }, [isEditMode]);

    const { action, icon } = EntityColors.gallery;
    const accentStyle = { '--gallery-accent': action } as React.CSSProperties;
    const { createGallery, getGallery, updateGallery, deleteGallery } = GalleryService();
    const { galleryId } = useParams<{ galleryId: string }>();
    const { currentSite }: any = useContext(SiteContext);
    const { setToast }: any = useContext(ToastContext);

    const [gallery, setGallery] = useState(Constants.defaultGallery(currentSite.id));
    const [deleteModal, setDeleteModal] = useState(false);
    const [showTitleImageModal, setShowTitleImageModal] = useState(false);
    const [showOtherImageModal, setShowOtherImageModal] = useState(false);
    const [showImageUploadModal, setShowImageUploadModal] = useState(false);
    const [isTitleLastUpload, setIsTitleLastUpload] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [imagePreview, setImagePreview] = useState<ImageFile | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [moved, setMoved] = useState<MovedImage | null>(null);
    const imageGridRef = useRef<HTMLDivElement>(null);
    const isDirty = useIsDirty(gallery, loaded);

    const doSave = async (): Promise<boolean> => {
        if (!gallery.titleImage) {
            setToast({ message: 'Vyberte titulný obrázok.' });
            return false;
        }

        const c = gallery.coordinates;
        if (!c?.filled || Math.min(c.left, c.up, c.right, c.down) < 0) {
            setToast({ message: 'Výrez titulného obrázka nie je nastavený.' });
            return false;
        }
        if (c.right - c.left < TITLE_CROP.width || c.down - c.up < TITLE_CROP.height) {
            setToast({ message: `Výrez musí mať aspoň ${TITLE_CROP.width} × ${TITLE_CROP.height} px — zvoľte väčší obrázok.` });
            return false;
        }

        setIsSaving(true);
        try {
            if (isEditMode) await updateGallery(gallery);
            else await createGallery(gallery, isPopUp, onClose);
        } finally {
            setIsSaving(false);
        }
        return true;
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        void doSave();
    };

    const handleLoad = async () => {
        const data = await getGallery(Number(galleryId));
        if (data) setGallery({ ...data, coordinates: null });
        setLoaded(true);
    };

    const handleDelete = async () => {
        await deleteGallery(gallery.id);
    };

    /** A picked image becomes a pending usage (id 0) until the backend crops and stores it. */
    const toPendingUsage = (image: ImageFile): ImageUsage => ({
        id: 0,
        imageId: image.id,
        filePath: image.filePath,
        alt: image.alt,
    });

    const handleTitleImageSelect = (image: ImageFile) => {
        setImagePreview(null);
        setShowTitleImageModal(false);
        if (gallery.titleImage?.imageId === image.id) return;
        setGallery(prev => ({
            ...prev,
            titleImage: toPendingUsage(image),
            coordinates: null,
            galleryImages: prev.galleryImages.filter(usage => usage.imageId !== image.id),
        }));
    };

    /** Clicking an image in the picker adds it; clicking it again takes it back out. */
    const handleOtherImageToggle = (image: ImageFile) => {
        if (gallery.titleImage?.imageId === image.id) return;
        setGallery(prev => prev.galleryImages.some(usage => usage.imageId === image.id)
            ? { ...prev, galleryImages: prev.galleryImages.filter(usage => usage.imageId !== image.id) }
            : { ...prev, galleryImages: [...prev.galleryImages, toPendingUsage(image)] });
    };

    const handleOtherImageRemove = (imageId: number) => {
        setGallery(prev => ({ ...prev, galleryImages: prev.galleryImages.filter(usage => usage.imageId !== imageId) }));
    };

    /** The previous title image takes the promoted image's place, so the rest of the order stays put. */
    const handleOtherImageSetAsTitle = (usage: ImageUsage) => {
        setGallery(prev => ({
            ...prev,
            titleImage: usage,
            coordinates: null,
            galleryImages: prev.titleImage
                ? prev.galleryImages.map(u => u.imageId === usage.imageId ? prev.titleImage! : u)
                : prev.galleryImages.filter(u => u.imageId !== usage.imageId),
        }));
    };

    /** Moves an image to a 1-based position; the pager follows it so it stays in view. */
    const handleOtherImageMove = (imageId: number, position: number, refocus?: MoveControl) => {
        const from = gallery.galleryImages.findIndex(usage => usage.imageId === imageId);
        const to = Math.min(Math.max(position, 1), gallery.galleryImages.length) - 1;
        if (from === -1 || from === to) return;

        setGallery(prev => ({ ...prev, galleryImages: moveItem(prev.galleryImages, from, to) }));
        setCurrentPage(Math.floor(to / IMAGES_PER_PAGE) + 1);
        setMoved({ imageId, key: Date.now(), refocus });
    };

    // Moving re-inserts the card's DOM node, which drops focus: hand it back to the control that was used.
    useEffect(() => {
        if (!moved) return;
        if (moved.refocus) {
            const card = imageGridRef.current?.querySelector(`[data-image-id="${moved.imageId}"]`);
            const control = card?.querySelector<HTMLElement>(`[data-move="${moved.refocus}"]:not(:disabled)`)
                ?? card?.querySelector<HTMLElement>('[data-move]:not(:disabled)');
            control?.focus();
        }
        const timer = window.setTimeout(() => setMoved(null), 900);
        return () => window.clearTimeout(timer);
    }, [moved]);

    useEffect(() => {
        if (isEditMode) void handleLoad();
        else setLoaded(true);
    }, [isEditMode, galleryId]);

    const allImages = Array.isArray(gallery.galleryImages) ? gallery.galleryImages : [];
    const totalPages = Math.ceil(allImages.length / IMAGES_PER_PAGE);
    const startIdx = (currentPage - 1) * IMAGES_PER_PAGE;
    const displayedImages = allImages.slice(startIdx, startIdx + IMAGES_PER_PAGE);

    // Removing images can leave the pager past the last page
    useEffect(() => {
        if (totalPages === 0 && currentPage !== 1) setCurrentPage(1);
        else if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages]);

    const selectedImageIds = [
        ...(gallery.titleImage ? [gallery.titleImage.imageId] : []),
        ...allImages.map(usage => usage.imageId),
    ];

    const formContent = (
        <>
            {!loaded ? (
                <p className="text-center py-4 text-secondary">Načítavam...</p>
            ) : (
                <form onSubmit={handleSubmit} className="st-gallery-form" style={accentStyle}>
                    <Form.Group>
                        <div className="st-field-head">
                            <RequiredLabel htmlFor="headline">Nadpis</RequiredLabel>
                        </div>
                        <Form.Control
                            id="headline"
                            name="headline"
                            required
                            autoFocus
                            value={gallery.title}
                            onChange={(e: any) => setGallery({ ...gallery, title: e.target.value })}
                        />
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <ToggleSwitch
                            id="galleryVisibility"
                            label="Viditeľnosť galérie"
                            onLabel="Verejná"
                            offLabel="Skrytá"
                            checked={gallery.visibility === 'Public'}
                            onChange={next => setGallery({ ...gallery, visibility: next ? 'Public' : 'Hidden' })}
                            accentColor={action}
                        />
                        <p className="text-muted small mt-2 mb-0">
                            Aj skrytú galériu je možné priradiť k príspevku. Nezobrazí sa však na stránke
                            albumov, kde sú vypísané všetky verejné galérie.
                        </p>
                    </Form.Group>

                    <SectionHeader
                        label="Titulný obrázok"
                        actionSlot={gallery.titleImage && (
                            <button type="button" className="st-btn st-btn--outline st-btn--sm" onClick={() => setShowTitleImageModal(true)}>
                                Vybrať iný
                            </button>
                        )}
                    />

                    {gallery.titleImage ? (
                        <GalleryTitleImageCropper gallery={gallery} setGallery={setGallery} />
                    ) : (
                        <DashedTile
                            icon={faImage}
                            label="Vybrať titulný obrázok"
                            sub={`Obálka albumu · výrez ${TITLE_CROP.width} × ${TITLE_CROP.height} px`}
                            onClick={() => setShowTitleImageModal(true)}
                        />
                    )}

                    <SectionHeader
                        label={`Ďalšie obrázky · ${allImages.length}`}
                        actionSlot={
                            <button
                                type="button"
                                className="st-btn st-btn--tint st-btn--sm"
                                onClick={() => setShowOtherImageModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="st-gallery-section__add-icon" />
                                Pridať
                            </button>
                        }
                    />

                    {allImages.length > 1 && (
                        <p className="st-gallery-hint">
                            Na webe sa zobrazia v tomto poradí. Posúvajte ich šípkami alebo kliknite na číslo a zadajte novú pozíciu.
                        </p>
                    )}

                    <Row xs={2} md={4} className="g-3" ref={imageGridRef}>
                        {displayedImages.map((usage, i) => (
                            <Col key={usage.imageId}>
                                <GalleryImageCard
                                    usage={usage}
                                    position={startIdx + i + 1}
                                    total={allImages.length}
                                    thumbnailUrl={Utils.getThumbnailImageFilePath(currentSite.id, usage.imageId, 180, 120)}
                                    highlightKey={moved?.imageId === usage.imageId ? moved.key : undefined}
                                    onMove={(position, refocus) => handleOtherImageMove(usage.imageId, position, refocus)}
                                    onRemove={() => handleOtherImageRemove(usage.imageId)}
                                    onMakeTitle={() => handleOtherImageSetAsTitle(usage)}
                                />
                            </Col>
                        ))}
                        {currentPage === Math.max(totalPages, 1) && (
                            <Col>
                                <DashedTile
                                    icon={faImages}
                                    label={allImages.length === 0 ? 'Pridať obrázky' : 'Pridať'}
                                    fill
                                    onClick={() => setShowOtherImageModal(true)}
                                />
                            </Col>
                        )}
                    </Row>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} />
                                {[...Array(totalPages)].map((_, idx) => (
                                    <Pagination.Item key={idx + 1} active={currentPage === idx + 1} onClick={() => setCurrentPage(idx + 1)}>
                                        {idx + 1}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} />
                            </Pagination>
                        </div>
                    )}

                    {/* Title image picker */}
                    <Modal show={showTitleImageModal} onHide={() => setShowTitleImageModal(false)} dialogClassName="modal-xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Vybrať titulný obrázok</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <ImageSelectorComponent
                                accentColor={action}
                                selectedIds={gallery.titleImage ? [gallery.titleImage.imageId] : []}
                                handleImageClick={(img: ImageFile) => {
                                    setImagePreview(img);
                                    setShowTitleImageModal(false);
                                }}
                                refreshTrigger={refreshTrigger}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <CreateButton color={action} onClick={() => {
                                setIsTitleLastUpload(true);
                                setShowTitleImageModal(false);
                                setShowImageUploadModal(true);
                            }} label="Nahrať nový obrázok" />
                            <Button variant="secondary" onClick={() => setShowTitleImageModal(false)}>Zavrieť výber</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Gallery images picker (click adds, click again removes) */}
                    <Modal show={showOtherImageModal} onHide={() => setShowOtherImageModal(false)} dialogClassName="modal-xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Pridať obrázky do galérie</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <ImageSelectorComponent
                                accentColor={action}
                                selectedIds={selectedImageIds}
                                handleImageClick={handleOtherImageToggle}
                                refreshTrigger={refreshTrigger}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <CreateButton color={action} onClick={() => {
                                setIsTitleLastUpload(false);
                                setShowOtherImageModal(false);
                                setShowImageUploadModal(true);
                            }} label="Nahrať nový obrázok" />
                            <Button variant="secondary" onClick={() => setShowOtherImageModal(false)}>Hotovo</Button>
                        </Modal.Footer>
                    </Modal>

                    <div className="d-flex justify-content-center mt-4 gap-3">
                        {isEditMode && <DeleteButton onClick={() => setDeleteModal(true)} disabled={isSaving} />}
                        {isPopUp
                            ? <DiscardButton onClick={onClose} disabled={isSaving} isDirty={isDirty} />
                            : <DiscardButton to="/gallery/table" disabled={isSaving} isDirty={isDirty} />}
                        <SaveButton type="submit" isLoading={isSaving} onSave={doSave} websiteId={currentSite.id} />
                    </div>
                </form>
            )}
        </>
    );

    const sharedModals = (
        <>
            <Modal show={showImageUploadModal} onHide={() => {
                setShowImageUploadModal(false);
                if (isTitleLastUpload) setShowTitleImageModal(true);
                else setShowOtherImageModal(true);
                setRefreshTrigger(refreshTrigger + 1);
            }} dialogClassName="modal-xl">
                <Modal.Body className="pb-4">
                    <ImageUploadComponent isPopUp={true} onClose={() => {
                        setShowImageUploadModal(false);
                        if (isTitleLastUpload) setShowTitleImageModal(true);
                        else setShowOtherImageModal(true);
                        setRefreshTrigger(refreshTrigger + 1);
                    }} />
                </Modal.Body>
            </Modal>

            {imagePreview != null && (
                <Modal show onHide={() => {
                    setImagePreview(null);
                    setShowTitleImageModal(true);
                }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Náhľad obrázka</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        <img className="img-fluid st-crop-preview-img" src={imagePreview.filePath} alt={imagePreview.alt} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => {
                            setImagePreview(null);
                            setShowTitleImageModal(true);
                        }}>Zrušiť</Button>
                        <Button variant="success" onClick={() => handleTitleImageSelect(imagePreview)}>
                            Použiť ako titulný
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            <GalleryDeleteModal show={deleteModal} onHide={() => setDeleteModal(false)} onSubmit={handleDelete} idToDelete={gallery.id} />
        </>
    );

    if (isPopUp) {
        return (
            <>
                <div className="st-gallery-popup" style={accentStyle}>
                    <div className="st-gallery-popup__head">
                        <div className="st-gallery-popup__icon">
                            <FontAwesomeIcon icon={icon} />
                        </div>
                        <h2 className="st-gallery-popup__title">
                            {isEditMode ? 'Upraviť galériu' : 'Vytvoriť galériu'}
                        </h2>
                        <button
                            type="button"
                            title="Zavrieť"
                            className="st-gallery-popup__close"
                            onClick={() => { if (!isDirty || confirmDiscardChanges()) onClose(); }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    {formContent}
                </div>
                {sharedModals}
            </>
        );
    }

    return (
        <>
            <FormShell
                title={isEditMode ? 'Upraviť galériu' : 'Vytvoriť galériu'}
                icon={icon}
                accentColor={action}
                backHref="/gallery/table"
                isDirty={isDirty}
            >
                {formContent}
            </FormShell>
            {sharedModals}
        </>
    );
};

/** Eyebrow-voiced section divider: label, silk rule, optional action on the right. */
const SectionHeader = ({ label, actionSlot }: { label: string; actionSlot?: React.ReactNode }) => (
    <div className="st-gallery-section">
        <span className="st-eyebrow st-gallery-section__label">{label}</span>
        <div className="st-gallery-section__rule" />
        {actionSlot}
    </div>
);

/** Dashed invitation tile used for the empty title slot and the add-images cell. */
const DashedTile = ({
    icon,
    label,
    sub,
    fill = false,
    onClick,
}: {
    icon: IconDefinition;
    label: string;
    sub?: string;
    /** Stretch to the height of the image cards next to it. */
    fill?: boolean;
    onClick: () => void;
}) => (
    <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
            }
        }}
        className={`st-dropzone${fill ? ' st-dropzone--fill' : ''}`}
    >
        <FontAwesomeIcon icon={icon} className="st-dropzone__icon" />
        <span className="st-dropzone__label">{label}</span>
        {sub && <span className="st-dropzone__sub">{sub}</span>}
    </div>
);

/** One gallery image: thumbnail with its position and move arrows, alt caption, and promote/remove actions. */
const GalleryImageCard = ({
    usage,
    position,
    total,
    thumbnailUrl,
    highlightKey,
    onMove,
    onRemove,
    onMakeTitle,
}: {
    usage: ImageUsage;
    /** 1-based place in the gallery. */
    position: number;
    total: number;
    thumbnailUrl: string;
    /** Set while the card has just moved; a new value replays the highlight. */
    highlightKey?: number;
    onMove: (position: number, refocus?: MoveControl) => void;
    onRemove: () => void;
    onMakeTitle: () => void;
}) => (
    <div className="st-gimg" data-image-id={usage.imageId}>
        {highlightKey !== undefined && <span key={highlightKey} className="st-gimg__flash" aria-hidden="true" />}
        <div className="st-gimg__media">
            <img className="st-gimg__img" src={thumbnailUrl} alt={usage.alt} loading="lazy" />
            <PositionBadge position={position} total={total} onMove={onMove} />
            {total > 1 && (
                <div className="st-gimg__move">
                    <button
                        type="button"
                        className="st-gimg__move-btn"
                        data-move="prev"
                        title="Posunúť dopredu"
                        aria-label="Posunúť dopredu"
                        disabled={position === 1}
                        onClick={() => onMove(position - 1, 'prev')}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        type="button"
                        className="st-gimg__move-btn"
                        data-move="next"
                        title="Posunúť dozadu"
                        aria-label="Posunúť dozadu"
                        disabled={position === total}
                        onClick={() => onMove(position + 1, 'next')}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            )}
        </div>
        <div className="st-gimg__foot">
            <small className="st-gimg__alt text-truncate" title={usage.alt}>
                {usage.alt || 'Bez popisu'}
            </small>
            <div className="st-gimg__actions">
                <CustomTooltip message="Nastaviť ako titulný">
                    <button type="button" className="st-btn st-btn--tint st-btn--icon st-btn--sm" onClick={onMakeTitle}>
                        <FontAwesomeIcon icon={faStar} className="st-gimg__action-icon" />
                    </button>
                </CustomTooltip>
                <CustomTooltip message="Odobrať z galérie">
                    <span className="d-inline-flex">
                        <DeleteButton iconOnly className="st-btn--sm" onClick={onRemove} />
                    </span>
                </CustomTooltip>
            </div>
        </div>
    </div>
);

/** The image's place in the gallery; click it to type a new position. */
const PositionBadge = ({ position, total, onMove }: {
    position: number;
    total: number;
    onMove: (position: number, refocus?: MoveControl) => void;
}) => {
    const [draft, setDraft] = useState<string | null>(null);
    const editingRef = useRef(false);
    const refocusRef = useRef(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Enter/Escape unmount the focused input; put focus back on the badge.
    useEffect(() => {
        if (draft !== null || !refocusRef.current) return;
        refocusRef.current = false;
        buttonRef.current?.focus();
    }, [draft]);

    const startEditing = () => {
        editingRef.current = true;
        setDraft(String(position));
    };

    /** Runs once per edit: Enter and blur apply the typed position, Escape discards it. */
    const finishEditing = (apply: boolean, fromKeyboard: boolean) => {
        if (!editingRef.current) return;
        editingRef.current = false;
        refocusRef.current = fromKeyboard;
        const target = Number.parseInt(draft ?? '', 10);
        setDraft(null);
        if (apply && !Number.isNaN(target) && target !== position) onMove(target, fromKeyboard ? 'position' : undefined);
    };

    if (draft !== null) {
        return (
            <input
                type="number"
                inputMode="numeric"
                className="st-gimg__order st-gimg__order--input"
                min={1}
                max={total}
                value={draft}
                autoFocus
                aria-label={`Nová pozícia (1 až ${total})`}
                onFocus={e => e.target.select()}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                    if (e.key !== 'Enter' && e.key !== 'Escape') return;
                    // Keeps Enter from submitting the form and Escape from closing the popup modal.
                    e.preventDefault();
                    finishEditing(e.key === 'Enter', true);
                }}
                onBlur={() => finishEditing(true, false)}
            />
        );
    }

    return (
        <button
            ref={buttonRef}
            type="button"
            className="st-gimg__order"
            data-move="position"
            title="Zmeniť pozíciu"
            aria-label={`Pozícia ${position} z ${total}, zmeniť`}
            disabled={total < 2}
            onClick={startEditing}
        >
            {position}
        </button>
    );
};

export default GalleryCreateEditComponent;
